import { Component } from '@theme/component';

/**
 * Color highlighters: tabs + slider/grid of colors from group_colors metaobjects.
 * Slider uses theme slideshow-component; we replace slide content on tab change so drag/scroll stay theme-driven.
 * View-all button: base URL + ?filters=<handle>.
 * Copy: `data-view-all-default` and `data-view-all-tab-template` come from theme locale strings (see blocks.color_highlighters).
 */
const VIEW_ALL_TAB_PLACEHOLDER = '@@VIEW_ALL_TAB@@';

class ColorHighlighters extends Component {
  /** @type {{ template: HTMLTemplateElement; filters: string[]; isHighlight: boolean; order: number; updatedAt: number }[]} */
  #colors = [];

  /** @type {HTMLUListElement | HTMLDivElement | null} */
  #list = null;

  /** @type {HTMLButtonElement | null} */
  #viewAllButton = null;

  /** @type {string} */
  #activeFilter = 'highlights';

  /** @type {boolean} */
  #isSlider = false;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {string} */
  #initialSlidesHTML = '';

  connectedCallback() {
    super.connectedCallback();

    this.#isSlider = this.getAttribute('data-layout') === 'slider';
    this.#list = this.querySelector('[data-list]');
    this.#viewAllButton = this.querySelector('[data-view-all]');

    if (this.#isSlider) {
      const slideshow = this.querySelector('slideshow-component');
      this.#scroller = slideshow?.querySelector('slideshow-slides') ?? null;
      if (this.#scroller?.children.length) {
        this.#initialSlidesHTML = this.#scroller.innerHTML;
      }
    }

    this.#collectTemplates();
    this.#bindTabs();
    this.#renderForFilter('highlights', '');
  }

  #collectTemplates() {
    const templates = /** @type {NodeListOf<HTMLTemplateElement>} */ (
      this.querySelectorAll('template[data-color-template]')
    );
    this.#colors = Array.from(templates).map((t) => {
      const filters = (t.dataset.colorFilters || '')
        .split(',')
        .map((v) => String(v).trim())
        .filter(Boolean);
      return {
        template: t,
        filters,
        isHighlight: t.dataset.isHighlight === 'true',
        order: Number(t.dataset.order || '0') || 0,
        updatedAt: Number(t.dataset.updatedAt || '0') || 0,
      };
    });
  }

  /**
   * Sort by updatedAt descending (newest first). When updatedAt is 0, fall back to order ascending (lower order first).
   * @param {{ updatedAt: number; order: number }} a
   * @param {{ updatedAt: number; order: number }} b
   * @returns {number}
   */
  #sortByUpdatedAtThenOrder(a, b) {
    const aHasDate = a.updatedAt > 0;
    const bHasDate = b.updatedAt > 0;
    if (aHasDate && bHasDate) return b.updatedAt - a.updatedAt;
    if (aHasDate && !bHasDate) return -1;
    if (!aHasDate && bHasDate) return 1;
    return a.order - b.order;
  }

  #bindTabs() {
    this.querySelectorAll('button[data-filter]').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
        this.#setActiveTab(btn);
        this.#renderForFilter(btn.dataset.filter || 'highlights', btn.dataset.filterLabel || '');
      });
    });
  }

  /** @param {HTMLButtonElement} active */
  #setActiveTab(active) {
    this.querySelectorAll('button[data-filter]').forEach((tab) => {
      const btn = /** @type {HTMLButtonElement} */ (tab);
      const isActive = btn === active;
      btn.classList.toggle('color-highlighters__tab--active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  /**
   * @param {{ template: HTMLTemplateElement; filters: string[]; isHighlight: boolean; order: number; updatedAt: number }[]} candidates
   */
  #injectSlidesIntoScroller(candidates) {
    if (!this.#scroller) return;
    const fragment = document.createDocumentFragment();
    candidates.forEach((c, i) => {
      const cardContent = c.template.content.firstElementChild?.firstElementChild;
      if (!cardContent) return;
      const slide = document.createElement('div');
      slide.className = 'card carousel-color-card';
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('slide-id', String(i));
      slide.setAttribute('data-carousel-card', '');
      slide.appendChild(cardContent.cloneNode(true));
      fragment.appendChild(slide);
    });
    this.#scroller.innerHTML = '';
    this.#scroller.appendChild(fragment);
    this.#scroller.scrollLeft = 0;
  }

  /**
   * @param {string} filter
   * @param {string} [activeTabLabel] - Label of the active tab (for view-all button text).
   */
  #renderForFilter(filter, activeTabLabel = '') {
    const wasOnOtherTab = this.#activeFilter !== 'highlights';
    this.#activeFilter = filter;

    let candidates =
      filter === 'highlights'
        ? this.#colors.filter((c) => c.isHighlight)
        : this.#colors.filter((c) => c.filters.includes(filter));

    if (filter === 'highlights' && candidates.length === 0) {
      candidates = [...this.#colors];
    }
    candidates.sort(this.#sortByUpdatedAtThenOrder);
    const limit = parseInt(this.getAttribute('data-cards-limit') || '5', 10) || 5;
    candidates = candidates.slice(0, limit);

    if (this.#isSlider && this.#scroller) {
      if (filter === 'highlights' && wasOnOtherTab && this.#initialSlidesHTML) {
        this.#scroller.innerHTML = this.#initialSlidesHTML;
        this.#scroller.scrollLeft = 0;
      } else if (filter !== 'highlights') {
        this.#injectSlidesIntoScroller(candidates);
      } else if (filter === 'highlights' && candidates.length > 0 && this.#scroller.children.length === 0) {
        /* No server-rendered slides (e.g. no highlights selected); inject fallback so cards show on load */
        this.#injectSlidesIntoScroller(candidates);
        this.#initialSlidesHTML = this.#scroller.innerHTML;
      }
    } else if (this.#list) {
      const list = this.#list;
      list.innerHTML = '';
      candidates.forEach((c) => {
        const clone = c.template.content.firstElementChild?.cloneNode(true);
        if (clone) list.appendChild(clone);
      });
    }

    this.#updateViewAllLink(activeTabLabel);
  }

  /**
   * @param {string} [activeTabLabel] - When set (non-highlights tab), button shows "View all {label}".
   */
  #updateViewAllLink(activeTabLabel = '') {
    const baseUrl = this.getAttribute('data-base-url');
    const btn = this.#viewAllButton;
    if (!btn || !baseUrl) return;

    const defaultLabel = btn.dataset.viewAllDefault || '';
    const tabTemplate = this.dataset.viewAllTabTemplate || '';

    if (this.#activeFilter !== 'highlights' && activeTabLabel && tabTemplate.includes(VIEW_ALL_TAB_PLACEHOLDER)) {
      btn.textContent = tabTemplate.split(VIEW_ALL_TAB_PLACEHOLDER).join(activeTabLabel);
    } else {
      btn.textContent = defaultLabel;
    }

    const url = new URL(baseUrl || '', window.location.origin);
    if (this.#activeFilter !== 'highlights') {
      url.searchParams.set('filters', this.#activeFilter);
    } else {
      url.searchParams.delete('filters');
    }
    btn.onclick = () => {
      window.location.href = url.toString();
    };
  }
}

if (!customElements.get('color-highlighters')) {
  customElements.define('color-highlighters', ColorHighlighters);
}
